import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import { Button } from 'atoms/buttons/Button';
import { BodyM } from 'atoms/typography';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { SocialShare } from 'components/user/SocialShare';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useIntlContext } from 'contexts/intl';
import type { SocialShareEventContext } from 'lib/events';
import { socialShareEventName } from 'lib/events';
import { useEvents } from 'lib/events/useEvents';
import { glossary } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

const messages = defineMessages({
  share: {
    id: 'InviteFriendsCta.shareLink',
    defaultMessage: 'Share your link',
  },
});

type Props = {
  source: SocialShareEventContext;
  referralUrl?: string;
  hideShareButton?: boolean;
};

const Root = styled.div`
  display: flex;
  flex-direction: column;
  color: var(--c-nd-600);
  gap: var(--unit);
  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background-color: var(--c-nd-100);
    padding: var(--unit);
    border-radius: 2em;
  }
`;
const Copy = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--unit);
  flex-direction: row;
  overflow: hidden;
  background-color: var(--c-nd-100);
  padding: var(--unit) var(--double-unit);
  border-radius: 2em;
  @media ${tabletAndAbove} {
    padding: 0;
    border: none;
    background: none;
  }
`;
const ReferralLinkAddress = styled(Ellipsis)`
  color: var(--c-white);
  padding-left: var(--double-unit);
`;
const ButtonCopy = styled(Button)`
  color: var(--c-white);
  flex-shrink: 0;
  &:hover {
    background: var(--c-nd-50);
    color: var(--c-white);
  }
`;

export const InviteFriendsCta = ({
  source,
  referralUrl: referralUrlFromProps,
  hideShareButton,
}: Props) => {
  const { formatMessage } = useIntlContext();
  const { currentUser } = useCurrentUserContext();
  const track = useEvents();

  if (!currentUser) return null;

  const referralUrl = referralUrlFromProps || currentUser?.referralUrl || '';

  const trackClick = () => {
    track('Click Invite Friends', { source });
  };

  const copyToClipboardAndTrack = () => {
    navigator.clipboard.writeText(referralUrl);
    trackClick();
  };

  return (
    <Root>
      <Copy>
        <ReferralLinkAddress>{referralUrl}</ReferralLinkAddress>
        {!!navigator.clipboard && (
          <ButtonCopy
            size="medium"
            color="transparent"
            onClick={copyToClipboardAndTrack}
          >
            <BodyM bold>
              <FormattedMessage {...glossary.copy} />
            </BodyM>
          </ButtonCopy>
        )}
      </Copy>
      {!hideShareButton && (
        <SocialShare
          url={referralUrl}
          title={formatMessage(messages.share)}
          trackingEventName={socialShareEventName.SHARE_REFERRAL}
          trackingEventContext={source}
          renderButton={({ ShareButton, label }) => (
            <ShareButton color="primary" size="medium" onClick={trackClick}>
              {label}
            </ShareButton>
          )}
        />
      )}
    </Root>
  );
};
